// Vocabularies, copied from DP_Research_Hub.gs. Keep in step with the old app
// until cutover.

export const ROLE_OPTIONS = ['contributes', 'leads', 'supervises', 'reviews'] as const;

export const STATUS_OPTIONS = [
  'ACTIVE', 'IDEA', 'PENDING', 'PAUSED', 'DONE', 'APPLIED', 'FOLLOW', 'SEEKING',
] as const;

/**
 * Node types that count as tasks for actions and the stalled list (Actions.gs:24).
 * Also the category list a task may take. FundingProject is left out on purpose:
 * grants are made with addGrant / convertToGrant.
 */
export const TASK_TYPES = ['Topic', 'PhD', 'MS', 'BSc', 'Task', 'Initiative'] as const;

export const GRANT_TYPE = 'FundingProject';

/**
 * Longest free text a field may hold (descriptions, notes, comments). Azure Tables keeps up to
 * 32K characters in one property; a longer value is refused with a message, never cut.
 */
export const MAX_TEXT = 30_000;

/** Completed next actions a Gantt bundle carries per task (the ✓ after a bar): the newest, as the details panel lists them. */
export const DONE_RECENT = 5;

/** Longest folder notes (their HTML), in characters. Stored in two table properties. */
export const FOLDER_NOTES_MAX = 50_000;

/** Longest name of a file in a folder. */
export const FILE_NAME_MAX = 200;

export const OUTPUT_KINDS = ['Paper', 'App', 'Method', 'Dataset', 'Thesis', 'Talk'] as const;

export const OUTPUT_STAGES: Record<string, readonly string[]> = {
  Paper: ['idea', 'drafting', 'internal review', 'submitted', 'revision', 'published'],
  App: ['prototype', 'testing', 'pilot', 'released'],
  Method: ['draft', 'validation', 'SOP approved'],
  Dataset: ['collecting', 'curating', 'deposited', 'published'],
  Thesis: ['drafting', 'internal review', 'submitted', 'defended'],
  Talk: ['planned', 'accepted', 'delivered'],
  _default: ['planned', 'in progress', 'review', 'done'],
};

export function outputStages(kind: string): readonly string[] {
  return OUTPUT_STAGES[kind] ?? OUTPUT_STAGES._default!;
}

/** The last rung of a kind's ladder counts as landed. */
export function outputDone(kind: string, stage: string | null | undefined): boolean {
  const ladder = outputStages(kind);
  return String(stage ?? '').toLowerCase() === String(ladder[ladder.length - 1]).toLowerCase();
}

export const APP_STATUSES = [
  'submitted', 'under_review', 'shortlisted', 'accepted', 'declined', 'withdrawn',
] as const;

/** Academic statuses that make a registrant a mentor rather than a student. */
export const MENTOR_STATUSES = ['PostDoc', 'Faculty / Mentor', 'MD / Clinician'] as const;

// Hard-coded group sets (DP:2977, 2978, 6761).
export const SUGGEST_GROUPS = ['student', 'mentor', 'applicant', 'administrator', 'manager'] as const;
export const REVIEW_GROUPS = ['administrator', 'manager'] as const;
export const GANTT_FOCUS_GROUPS = ['administrator', 'manager'] as const;
/** Applicants sign themselves up (APPLY): their role is given no views in the role matrix. */
export const SELF_SIGNUP_GROUP = 'applicant';

/** Views with a row in the role matrix. `strategy` is new (no old page): read opens the Strategy tab, write changes the strategy and adds to it. */
export const VIEWS = [
  'home', 'gantt', 'outputs', 'comments', 'network', 'management', 'topics', 'admin', 'users', 'strategy',
] as const;
export type View = (typeof VIEWS)[number];
